/**
 * LEGACY / DEPRECATED (Pic2Content Core 1.0, Phase 0.5 — Generation 1).
 * Reachable only by direct URL (/assets/:itemId) — not linked from
 * AppLayout's nav. Do not build new product features on this page. See
 * docs/architecture/PIC2CONTENT_CORE_1_0.md.
 */
import { useEffect, useState } from 'react'
import { Button, Select, Space, Table, Tag, message } from 'antd'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  batchProcess,
  exportItem,
  getItem,
  listCategories,
  listShadowModes,
  updateItem,
} from '../api/client'
import type { Asset, Item, LabelValue } from '../types'
import { statusColor } from '../utils/status'

export default function ItemDetailPage() {
  const { itemId } = useParams()
  const [item, setItem] = useState<Item | null>(null)
  const [assets, setAssets] = useState<Asset[]>([])
  const [categories, setCategories] = useState<LabelValue[]>([])
  const [shadows, setShadows] = useState<LabelValue[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const navigate = useNavigate()

  const load = async () => {
    if (!itemId) return
    const [{ data }, { data: cats }, { data: sh }] = await Promise.all([
      getItem(itemId),
      listCategories(),
      listShadowModes(),
    ])
    setItem(data.item)
    setAssets(data.assets)
    setCategories(cats)
    setShadows(sh)
  }

  useEffect(() => {
    load().catch((e) => message.error(e.message))
    const t = setInterval(() => load().catch(() => undefined), 4000)
    return () => clearInterval(t)
  }, [itemId])

  if (!item) return null

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/assets">← 返回白底资产</Link>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>
            {item.item_code} · {item.display_name}
          </h2>
          <Tag color={statusColor[item.status]} style={{ marginTop: 8 }}>
            {item.status_label}
          </Tag>
        </div>
        <Space wrap>
          <Select
            style={{ width: 160 }}
            value={item.category}
            options={categories.map((c) => ({ value: c.value, label: c.label }))}
            onChange={async (category) => {
              const { data } = await updateItem(item.id, { category })
              setItem(data)
              message.success('分类已更新')
            }}
          />
          <Select
            style={{ width: 160 }}
            value={item.default_shadow_mode}
            options={shadows.map((c) => ({ value: c.value, label: c.label }))}
            onChange={async (default_shadow_mode) => {
              const { data } = await updateItem(item.id, { default_shadow_mode })
              setItem(data)
              message.success('默认阴影策略已更新')
            }}
          />
          <Button
            onClick={async () => {
              await batchProcess({
                item_ids: [item.id],
                asset_ids: selected.length ? selected : undefined,
              })
              message.success('抠图任务已排队')
            }}
          >
            {selected.length ? '批量抠图选中' : '全部自动抠图'}
          </Button>
          <Button
            type="primary"
            onClick={async () => {
              const { data } = await exportItem(item.id, { make_zip: true })
              message.success(`导出完成：${data.item_code}`)
            }}
          >
            导出本物品
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        dataSource={assets}
        rowSelection={{ selectedRowKeys: selected, onChange: (k) => setSelected(k as string[]) }}
        columns={[
          {
            title: '缩略图',
            width: 80,
            render: (_, r) =>
              r.thumbnail_url ? <img src={r.thumbnail_url} width={56} height={56} style={{ objectFit: 'cover' }} /> : '-',
          },
          { title: '文件名', dataIndex: 'original_filename' },
          {
            title: '尺寸',
            render: (_, r) => (r.width && r.height ? `${r.width}×${r.height}` : '-'),
          },
          {
            title: '状态',
            dataIndex: 'status',
            render: (s: string, r) => <Tag color={statusColor[s]}>{r.status_label || s}</Tag>,
          },
          {
            title: '透明/白底/阴影',
            render: (_, r) =>
              `${r.has_transparent ? '✓' : '–'} / ${r.has_white ? '✓' : '–'} / ${r.has_shadow ? '✓' : '–'}`,
          },
          { title: '版本', dataIndex: 'latest_version_number', width: 70 },
          {
            title: '最终',
            dataIndex: 'is_final',
            width: 70,
            render: (v: boolean) => (v ? '是' : '否'),
          },
          {
            title: '失败提示',
            dataIndex: 'error_message',
            ellipsis: true,
            render: (v: string) => v || '-',
          },
          {
            title: '操作',
            width: 120,
            render: (_, r) => (
              <Button type="link" onClick={() => navigate(`/editor/${item.id}/${r.id}`)}>
                进入编辑
              </Button>
            ),
          },
        ]}
      />
    </div>
  )
}
